/**
 * Claim mapping utilities for provider-agnostic authentication.
 *
 * Maps provider-specific token claims to canonical application claims.
 */

import {
  CanonicalUserClaims,
  ClaimMappingConfig,
  getClaimMapping,
  PROVIDER_CLAIM_MAPPINGS,
} from './providers/types';

/**
 * Map raw token claims to canonical user claims.
 *
 * @param rawClaims - Raw claims from the OIDC token
 * @param mapping - Claim mapping configuration for the provider
 * @param provider - Provider name ('cognito', 'entra', 'local')
 * @returns Canonical user claims
 */
export function mapClaims(
  rawClaims: Record<string, unknown>,
  mapping: ClaimMappingConfig,
  provider: string
): CanonicalUserClaims {
  // Extract user ID
  const sub = (rawClaims[mapping.userIdClaim] as string) || '';

  // Extract email
  const email = (rawClaims[mapping.emailClaim] as string) || '';

  // Extract groups (handle both array and single value)
  const groupsClaim = rawClaims[mapping.groupsClaim];
  let groups: string[];
  if (Array.isArray(groupsClaim)) {
    groups = groupsClaim as string[];
  } else if (typeof groupsClaim === 'string') {
    groups = [groupsClaim];
  } else {
    groups = [];
  }

  // Extract name
  const name = rawClaims[mapping.nameClaim] as string | undefined;

  return {
    sub,
    email,
    groups,
    name,
    provider,
  };
}

/**
 * Map claims from a specific provider.
 *
 * @param rawClaims - Raw claims from the OIDC token
 * @param provider - Provider name ('cognito', 'entra', 'local')
 * @returns Canonical user claims
 */
export function mapClaimsForProvider(
  rawClaims: Record<string, unknown>,
  provider: string
): CanonicalUserClaims {
  const mapping = getClaimMapping(provider);
  return mapClaims(rawClaims, mapping, provider);
}

/**
 * Check if a user has a specific group/role.
 *
 * @param claims - Canonical user claims
 * @param group - Group name to check
 * @returns True if the user has the group
 */
export function hasGroup(claims: CanonicalUserClaims, group: string): boolean {
  return claims.groups.includes(group);
}

/**
 * Check if a user has admin privileges.
 *
 * @param claims - Canonical user claims
 * @returns True if the user is an admin
 */
export function isAdmin(claims: CanonicalUserClaims): boolean {
  return hasGroup(claims, 'admin');
}

/**
 * Get the display name for a user.
 *
 * Falls back to email if name is not available.
 *
 * @param claims - Canonical user claims
 * @returns Display name or email
 */
export function getDisplayName(claims: CanonicalUserClaims): string {
  return claims.name || claims.email || claims.sub || 'Unknown User';
}

// Re-export types and constants for convenience
export { PROVIDER_CLAIM_MAPPINGS, getClaimMapping };
export type { CanonicalUserClaims, ClaimMappingConfig };
